import { api, fmtDate, fmtDuration } from "../api";
import { clear, h, modal, toast } from "../dom";
import type { Project, User } from "../types";

interface TileEntry {
  assignment_id: number; tile_id: number; basename: string; seconds: number;
  flags: number; flags_resolved: number; flags_waiting: number;
  review_rounds: number; review_note: string; submitted_at: number | null; reviewed_at: number | null;
}

interface TileLists {
  available: { id: number; basename: string }[];
  active: TileEntry[];
  review: TileEntry[];
  done: TileEntry[];
  paused: boolean;
  needs_review: boolean;
}

/** "corrections requested · round 2" badge with the reviewer's note as tooltip. */
function returnedBadge(t: TileEntry): HTMLElement | null {
  if (!t.review_rounds) return null;
  return h("span", { className: "review-badge", title: t.review_note ? `Reviewer's note: ${t.review_note}` : "Returned from review" },
    `↩ corrections requested · round ${t.review_rounds}`);
}

/** "⚑ 2 for you" / "⚑ 3/5 resolved" badge for a tile row. */
function flagBadge(t: TileEntry): HTMLElement | null {
  if (!t.flags) return null;
  if (t.flags_waiting) {
    return h("span", { className: "flag-count", title: `${t.flags_waiting} flag(s) wait for your reply or resolution — open the tile and press N` },
      `⚑ ${t.flags_waiting} for you`);
  }
  const allDone = t.flags_resolved === t.flags;
  return h("span", { className: "flag-count" + (allDone ? " flag-count-done" : " flag-count-quiet"),
    title: allDone ? "all flags resolved" : "flags on this tile, none waiting for you" },
    `⚑ ${t.flags_resolved}/${t.flags}`);
}

const LAST_PROJECT_KEY = "labeling.lastProject";

export function renderTiles(root: HTMLElement, user: User): void {
  const content = h("div", { className: "page" });
  root.append(
    h(
      "div",
      { className: "topbar" },
      h("span", { className: "brand" }, "Tree&Terrain Labeling"),
      h("span", { className: "spacer" }),
      h("span", { className: "muted" }, user.username),
      user.is_admin && h("button", { className: "btn btn-small", onClick: () => (location.hash = "#/admin") }, "Admin"),
      h("button", { className: "btn btn-small", title: "Your hours, rate, payments and open balance",
        onClick: () => (location.hash = "#/statement") }, "Statement"),
      h("button", { className: "btn btn-small", onClick: () => passwordDialog() }, "Password"),
      h(
        "button",
        {
          className: "btn btn-small",
          onClick: async () => {
            await api("/api/auth/logout", { method: "POST" });
            location.hash = "#/login";
            location.reload();
          },
        },
        "Log out",
      ),
    ),
    content,
  );
  void load(content);
}

async function load(content: HTMLElement): Promise<void> {
  const projects = await api<Project[]>("/api/projects");
  clear(content);
  if (projects.length === 0) {
    content.append(h("p", { className: "muted pad" }, "No projects yet. The admin needs to set one up."));
    return;
  }
  const saved = Number(localStorage.getItem(LAST_PROJECT_KEY));
  let project = projects.find((p) => p.id === saved) || projects[0];

  const body = h("div", {});
  const select = h(
    "select",
    {
      onChange: () => {
        project = projects.find((p) => p.id === Number(select.value))!;
        localStorage.setItem(LAST_PROJECT_KEY, select.value);
        void loadProject(body, project);
      },
    },
    ...projects.map((p) => h("option", { value: p.id, selected: p.id === project.id }, p.name)),
  );
  content.append(h("div", { className: "row gap pad" }, h("label", {}, "Project "), select), body);
  await loadProject(body, project);
}

async function loadProject(body: HTMLElement, project: Project): Promise<void> {
  const lists = await api<TileLists>(`/api/projects/${project.id}/tiles`);
  const time = await api<{ total_seconds: number }>("/api/me/time");
  clear(body);

  if (lists.paused) {
    body.append(h("div", { className: "banner" }, "Your account is paused — you can finish claimed tiles but not claim new ones."));
  }
  if (lists.needs_review) {
    body.append(h("p", { className: "muted pad small" },
      "Your tiles go through a review: submit a finished tile for review, an admin accepts it or returns it with flags to fix. "
      + "Tiles under review count towards your claim limit."));
  }

  const section = (title: string, rows: HTMLElement[], empty: string) =>
    h("section", { className: "pad" }, h("h2", {}, title), rows.length ? h("div", { className: "tile-list" }, rows) : h("p", { className: "muted" }, empty));

  const parts: (HTMLElement | null)[] = [
    section(
      "In progress",
      lists.active.map((t) =>
        h(
          "div",
          { className: "tile-row" },
          h("span", { className: "tile-name" }, t.basename),
          returnedBadge(t),
          flagBadge(t),
          h("span", { className: "muted" }, fmtDuration(t.seconds)),
          h("button", { className: "btn btn-primary btn-small", onClick: () => (location.hash = `#/label/${t.assignment_id}`) }, t.review_rounds ? "Fix" : "Continue"),
        ),
      ),
      "Nothing in progress.",
    ),
    section(
      "Available",
      lists.available.map((t) =>
        h(
          "div",
          { className: "tile-row" },
          h("span", { className: "tile-name" }, t.basename),
          h("button", {
            className: "btn btn-small",
            title: "Look at the tile (and its pre-labels) read-only, without claiming it",
            onClick: () => (location.hash = `#/view/${t.id}`),
          }, "View"),
          h(
            "button",
            {
              className: "btn btn-small",
              disabled: lists.paused,
              onClick: async () => {
                try {
                  const r = await api<{ assignment_id: number }>(`/api/tiles/${t.id}/claim`, { method: "POST" });
                  location.hash = `#/label/${r.assignment_id}`;
                } catch (e: any) {
                  toast(e.message, true);
                  void loadProject(body, project);
                }
              },
            },
            "Claim",
          ),
        ),
      ),
      "No unlabeled tiles available.",
    ),
    lists.review.length || lists.needs_review
      ? section(
          "Under review",
          lists.review.map((t) =>
            h(
              "div",
              { className: "tile-row" },
              h("span", { className: "tile-name" }, t.basename),
              h("span", { className: "muted" }, `submitted ${fmtDate(t.submitted_at)}` + (t.review_rounds ? ` · round ${t.review_rounds + 1}` : "")),
              flagBadge(t),
              h("span", { className: "muted" }, fmtDuration(t.seconds)),
              h("button", { className: "btn btn-small", onClick: () => (location.hash = `#/label/${t.assignment_id}`) }, "View"),
            ),
          ),
          "Nothing waiting for review.",
        )
      : null,
    section(
      "Done",
      lists.done.map((t) =>
        h(
          "div",
          { className: "tile-row" },
          h("span", { className: "tile-name" }, t.basename),
          flagBadge(t),
          h("span", { className: "muted" }, fmtDuration(t.seconds)),
          h("button", { className: "btn btn-small", onClick: () => (location.hash = `#/label/${t.assignment_id}`) }, "View"),
        ),
      ),
      "No finished tiles yet.",
    ),
    h("p", { className: "muted pad" }, `Total time across all projects: ${fmtDuration(time.total_seconds)}`),
  ];
  for (const p of parts) if (p) body.append(p);
}

function passwordDialog(): void {
  const oldPw = h("input", { type: "password", autocomplete: "current-password" });
  const newPw = h("input", { type: "password", autocomplete: "new-password", minLength: 8 });
  const newPw2 = h("input", { type: "password", autocomplete: "new-password" });
  const error = h("div", { className: "form-error" });
  const close = modal(
    h(
      "form",
      {
        onSubmit: async (e: Event) => {
          e.preventDefault();
          error.textContent = "";
          if (newPw.value !== newPw2.value) {
            error.textContent = "New passwords do not match";
            return;
          }
          try {
            await api("/api/auth/password", { method: "POST", body: { old_password: oldPw.value, new_password: newPw.value } });
            close();
            toast("Password changed");
          } catch (err: any) {
            error.textContent = err.message;
          }
        },
      },
      h("h2", {}, "Change password"),
      h("label", {}, "Current password", oldPw),
      h("label", {}, "New password (min. 8 characters)", newPw),
      h("label", {}, "Repeat new password", newPw2),
      error,
      h("div", { className: "row gap right" }, h("button", { className: "btn btn-primary", type: "submit" }, "Change")),
    ),
  );
}
